<template>
    <div class="card h-100" :style="fixedHeight ? { height: fixedHeight + 'px' } : {}">
        <div class="card-header ui05-header d-flex align-items-center justify-content-between gap-2 flex-wrap">
            <h3 class="card-title mb-0" style="font-size: 1.3rem;">Distribuzione costi per fornitore</h3>
            <select class="form-select form-select-sm ui05-azioni" v-model="azione" @change="eseguiAzione"
                    aria-label="Azioni sulla distribuzione spese per fornitore">
                <option value="">Seleziona</option>
                <option value="mostra">Mostra tutti</option>
                <option value="excel">Esporta Excel</option>
                <option value="pdf">Esporta PDF</option>
            </select>
        </div>
        <div class="card-body ui05-body" :class="{ 'ui05-body--fixed': fixedHeight }">
            <div v-if="loading" class="placeholder-glow text-center py-5">
                <span class="placeholder col-12 placeholder-lg"></span>
            </div>

            <BlockState v-else-if="nonDisponibile" stato="non_disponibile"
                        dettaglio="La distribuzione delle spese per fornitore non è disponibile per questa richiesta." />

            <p v-else-if="vuoto" class="text-muted text-center mb-0">
                Nessun costo fornitore nel periodo selezionato.
            </p>

            <template v-else>
                <!-- Il disegno e' delegato al wrapper <VChart> (autoresize): nessun
                     echarts.init, nessun listener di resize, nessun timer. -->
                <div class="ui05-chart" role="img" :aria-label="descrizioneAccessibile" :class="{ 'ui05-chart--fill': fixedHeight }">
                    <VChart class="ui05-canvas" :option="chartOption" autoresize />
                </div>
                <!-- La vecchia sezione "Dati in forma testuale" e' stata RIMOSSA:
                     il dettaglio vive ora nella drill modale. -->
            </template>
        </div>

        <!-- Drill modale: pattern di progetto (modal fade show + modal-dialog modal-lg).
             Mostra i dati REALI del blocco spese_fornitore senza richiamare l'API:
             quindi rappresenta esattamente il perimetro filtrato della Dashboard. -->
        <div v-if="mostraTutti" class="modal fade show ui05-modal" tabindex="-1" role="dialog"
             style="display: block; background: rgba(0, 0, 0, 0.5);">
            <div class="modal-dialog modal-lg modal-fullscreen-sm-down" role="document">
                <div class="modal-content">
                    <div class="modal-header bg-secondary text-white">
                        <h5 class="modal-title mb-0">Distribuzione spese per fornitore</h5>
                        <button type="button" class="btn-close btn-close-white" @click="chiudiTutti()"></button>
                    </div>
                    <div class="modal-body ui05-modal-body">
                        <!-- Riepilogo: solo grandezze gia' presenti nel payload.
                             3 box e periodo restano FISSI: scorre solo la tabella. -->
                        <div class="row g-2 mb-3">
                            <div class="col-6 col-md-3">
                                <div class="ui05-riepilogo">
                                    <span class="ui05-riepilogo-label">Fornitori</span>
                                    <span class="ui05-riepilogo-valore">{{ totaleFornitori }}</span>
                                </div>
                            </div>
                            <div class="col-6 col-md-3">
                                <div class="ui05-riepilogo">
                                    <span class="ui05-riepilogo-label">Totale costi</span>
                                    <span class="ui05-riepilogo-valore">{{ totaleImportoEur }}</span>
                                </div>
                            </div>
                            <div class="col-12 col-md-6">
                                <div class="ui05-riepilogo">
                                    <span class="ui05-riepilogo-label">Fornitore principale</span>
                                    <span class="ui05-riepilogo-valore">
                                        {{ fornitorePrincipale.nome }}
                                        <span class="text-muted">· {{ fornitorePrincipale.importoEur }}</span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div v-if="periodo" class="text-muted small mb-2">
                            Periodo dashboard: {{ periodo }}
                        </div>

                        <!-- Tabella COMPLETA: un fornitore per riga, gia' ordinata
                             dal backend per costo DECRESCENTE. Nessuna riga "Altri":
                             quell'aggregazione appartiene solo alla chart. -->
                        <div class="table-responsive ui05-tabella-wrap">
                            <table class="table table-sm font-size-sm mb-0 align-middle ui05-tabella">
                                <caption class="visually-hidden">Costo per fornitore nel periodo</caption>
                                <thead>
                                    <tr>
                                        <th scope="col" style="width: 3rem;">#</th>
                                        <th scope="col">Fornitore</th>
                                        <th scope="col" class="text-end" style="width: 9rem;">Costo</th>
                                        <th scope="col" class="text-end" style="width: 6rem;">Quota</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="r in tabella" :key="r.chiave">
                                        <td class="text-muted">{{ r.posizione }}</td>
                                        <th scope="row">{{ r.nome }}</th>
                                        <td class="text-end">{{ r.importoEur }}</td>
                                        <td class="text-end">{{ r.quota }}</td>
                                    </tr>
                                </tbody>
                                <tfoot v-if="tabella.length">
                                    <tr>
                                        <td colspan="2" class="text-end fw-semibold">Totale</td>
                                        <td class="text-end fw-semibold">{{ totaleImportoEur }}</td>
                                        <td class="text-end fw-semibold">100,0%</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                    <div class="modal-footer">
                        <button type="button" class="btn btn-sm btn-outline-secondary" @click="esportaExcel">
                            Esporta Excel
                        </button>
                        <button type="button" class="btn btn-sm btn-secondary" @click="chiudiTutti()">Chiudi</button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>


<script>
/* UI-05A - DISTRIBUZIONE SPESE PER FORNITORE.
 *
 * Nessun dato inventato: tutto deriva dal blocco `spese_fornitore` dello
 * STESSO payload della Dashboard (stessi filtri, stesso perimetro): la drill
 * e l'export non richiamano l'API e non fanno query dedicate.
 * Il backend usa la misura COSTO V1 di RDR-02, raggruppata per fornitore
 * (THEB_VOCI.FK_ID_AZIENDA_FORNITORE): nessuna formula economica qui.
 *
 * Palette e multicolor: puramente PRESENTAZIONALE, il colore e' assegnato per
 * posizione (1..N), mai a un fornitore specifico.
 * Export Excel e PDF: riusano il motore report gia' presente nel progetto
 * (LIB/ISPRINT_LIB/Report.php -> TCPDF / Excel), raggiunto dall'endpoint
 * /theb/report come fanno gli altri export. Nessuna dipendenza nuova e
 * nessun generatore PDF scritto a mano.
 */
import BlockState from "./BlockState.vue";
import VChart from "vue-echarts";
import { use } from "echarts/core";
import { BarChart } from "echarts/charts";
import { GridComponent, TooltipComponent } from "echarts/components";
import { CanvasRenderer } from "echarts/renderers";

/* Export: nessuna libreria lato client. PDF ed Excel sono prodotti dal motore
   report del progetto (LIB/ISPRINT_LIB/Report.php -> TCPDF / Excel). */
use([BarChart, GridComponent, TooltipComponent, CanvasRenderer]);

/* Palette dashboard: 10 colori, assegnati per posizione. */
const PALETTE = [
    "#4f7fe8", "#e97882", "#72aef0", "#e487a8", "#6fb584",
    "#e9ad55", "#d66f5f", "#68b9d9", "#7775bd", "#425b7a",
];
/* Colore neutro per la riga aggregata "Altri (...)". */
const COLORE_ALTRI = "#94a3b8";

const FORMAT_EUR = new Intl.NumberFormat("it-IT", {
    style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0,
});
const FORMAT_EUR_CENT = new Intl.NumberFormat("it-IT", {
    style: "currency", currency: "EUR", minimumFractionDigits: 2, maximumFractionDigits: 2,
});
const FORMAT_COMPATTO = new Intl.NumberFormat("it-IT", {
    notation: "compact", maximumFractionDigits: 1,
});
const FORMAT_PERC = new Intl.NumberFormat("it-IT", {
    style: "percent", maximumFractionDigits: 1,
});

export default {
    name: "SupplierSpendCard",
    components: { BlockState, VChart },

    props: {
        /* Blocco spese_fornitore del payload: null = non disponibile. */
        spese: { type: Object, default: null },
        loading: { type: Boolean, default: false },
        /* Etichetta del periodo corrente: solo presentazione (nome file, modal). */
        periodo: { type: String, default: "" },
        /* Filtri correnti della Dashboard: servono agli export, che devono
           riprodurre lo STESSO perimetro della schermata. */
        filtri: { type: Object, default: null },
        /* Altezza fissa (es. 420) per allinearsi a Classifiche. 0 = auto. */
        fixedHeight: { type: [Number, String], default: 0 },
    },

    data() {
        return {
            /* Menu azioni: torna SEMPRE allo stato neutro dopo l'esecuzione. */
            azione: "",
            mostraTutti: false,
        };
    },

    computed: {
        disponibile() {
            return !this.loading && this.spese !== null && this.spese !== undefined
                && Array.isArray(this.spese.top);
        },
        nonDisponibile() {
            return !this.loading && !this.disponibile;
        },
        vuoto() {
            return this.disponibile && this.righe.length === 0;
        },
        totaleImporto() {
            return this.disponibile && typeof this.spese.totale_importo === "number"
                ? this.spese.totale_importo : 0;
        },
        totaleImportoEur() {
            return FORMAT_EUR.format(this.totaleImporto);
        },
        totaleFornitori() {
            return this.disponibile && typeof this.spese.totale_fornitori === "number"
                ? this.spese.totale_fornitori : 0;
        },
        /* Somma delle voci esposte nel `top`: serve per l'eventuale riga "Altri". */
        sommaTop() {
            if (!this.disponibile) return 0;
            return this.spese.top.reduce((acc, r) => (
                acc + (r && typeof r.importo === "number" ? r.importo : 0)), 0);
        },
        /* Resto reale: totale di tutti i fornitori meno quelli gia' in elenco.
           Esiste solo se il backend dichiara l'elenco troncato (parziale). */
        restoImporto() {
            if (!this.disponibile || !this.spese.parziale) return null;
            const resto = this.totaleImporto - this.sommaTop;
            return resto > 0.005 ? resto : null;
        },
        righe() {
            if (!this.disponibile) return [];
            const out = this.spese.top
                .filter((r) => r && typeof r.importo === "number")
                .map((r, i) => this.riga(r.fornitore_id, r.denominazione, r.importo, "f", i));
            const resto = this.restoImporto;
            if (resto !== null) {
                const n = Math.max(0, this.totaleFornitori - out.length);
                out.push(this.riga("altri",
                    "Altri (" + n + (n === 1 ? " fornitore)" : " fornitori)"), resto, "a",
                    PALETTE.length));
            }
            return out;
        },
        /* UI-05: dataset COMPLETO per la drill. Il backend espone `dettaglio`
           (tutti i fornitori del perimetro, gia' ordinati per costo DESC):
           nessuna riga "Altri" e nessuna ricostruzione lato Vue.
           Fallback difensivo: se `dettaglio` manca si usa `top` (nessuna crash). */
        righeDettaglio() {
            if (!this.disponibile) return [];
            const src = Array.isArray(this.spese.dettaglio) ? this.spese.dettaglio : this.spese.top;
            return src
                .filter((r) => r && typeof r.importo === "number")
                .map((r, i) => this.riga(r.fornitore_id, r.denominazione, r.importo, "f", i));
        },
        /* Numero di fornitori realmente elencati nella drill. */
        totaleDettaglio() {
            return this.righeDettaglio.length;
        },
        /* Fornitore principale = primo della classifica reale (costo DESC). */
        fornitorePrincipale() {
            const primo = this.righeDettaglio[0];
            return primo || { nome: "Non attribuito", importoEur: "" };
        },
        /* Elenco della tabella drill: uno per fornitore, gia' in ordine di costo. */
        tabella() {
            return this.righeDettaglio.map((r, i) => Object.assign({}, r, { posizione: i + 1 }));
        },
        chartOption() {
            /* Barre orizzontali: il nome del fornitore resta leggibile anche in
               una colonna stretta. La prima riga (importo maggiore) va in alto,
               quindi l'ordine delle categorie e' invertito. */
            const dati = this.righe.slice().reverse();
            return {
                grid: { left: 4, right: 92, top: 8, bottom: 4, containLabel: true },
                tooltip: {
                    trigger: "axis",
                    axisPointer: { type: "shadow" },
                    /* Nome COMPLETO del fornitore (l'asse e' troncato). */
                    formatter: this.formatterTooltip,
                },
                xAxis: {
                    type: "value",
                    splitLine: { lineStyle: { color: "#eef2f7" } },
                    axisLabel: { formatter: (v) => FORMAT_COMPATTO.format(v) },
                },
                yAxis: {
                    type: "category",
                    data: dati.map((r) => r.nome),
                    axisTick: { show: false },
                    /* Nome troncato: presentazione, il nome reale resta nel tooltip. */
                    axisLabel: { fontSize: 11, width: 120, overflow: "truncate" },
                },
                series: [{
                    type: "bar",
                    /* Colore per posizione (palette), mai per fornitore. */
                    data: dati.map((r) => ({ value: r.importo, itemStyle: { color: r.colore } })),
                    barMaxWidth: 16,
                    itemStyle: { borderRadius: [0, 3, 3, 0] },
                    label: {
                        show: true,
                        position: "right",
                        fontSize: 10,
                        color: "#6c757d",
                        formatter: (p) => FORMAT_EUR.format(p.value),
                    },
                }],
            };
        },
        descrizioneAccessibile() {
            if (this.vuoto) return "Nessun costo fornitore nel periodo selezionato.";
            const prime = this.righe.slice(0, 3)
                .map((r) => r.nome + " " + r.importoEur).join("; ");
            return "Spese per fornitore nel periodo. Totale " + this.totaleImportoEur
                + " su " + this.totaleFornitori + " fornitori. Principali: " + prime + ".";
        },
    },

    methods: {
        riga(id, denominazione, importo, prefisso, indice = 0) {
            const nome = (denominazione && String(denominazione).trim()) || "Non attribuito";
            const quota = this.totaleImporto > 0 ? (importo / this.totaleImporto) : 0;
            /* Colore per POSIZIONE: la riga aggregata "Altri" usa il neutro. */
            const colore = (prefisso === "a")
                ? COLORE_ALTRI
                : PALETTE[indice % PALETTE.length];
            return {
                chiave: prefisso + "-" + (id === null || id === undefined ? "na" : id),
                nome,
                importo,
                importoEur: FORMAT_EUR.format(importo),
                quota: FORMAT_PERC.format(quota),
                quotaNum: quota,
                colore,
                aggregato: prefisso === "a",
            };
        },
        /* Tooltip: nome COMPLETO + totale formattato it-IT. */
        formatterTooltip(params) {
            if (!Array.isArray(params) || params.length === 0) return "";
            const p = params[0] || {};
            const r = this.righe.find((x) => x.nome === p.name);
            const nome = (p && p.name) ? p.name : "";
            const v = (p && typeof p.value === "number") ? p.value
                : (r ? r.importo : 0);
            const totale = (r ? FORMAT_EUR_CENT.format(r.importo) : FORMAT_EUR_CENT.format(v));
            return '<div style="font-weight:600">' + nome + "</div>"
                + '<div>Totale: ' + totale + "</div>";
        },
        /* Menu azioni: dopo l'esecuzione torna SEMPRE allo stato neutro. */
        eseguiAzione() {
            const a = this.azione;
            this.azione = "";
            if (a === "mostra") { this.mostraTutti = true; return; }
            if (a === "excel") { this.esportaExcel(); return; }
            if (a === "pdf") { this.esportaPdf(); return; }
        },
        chiudiTutti() {
            this.mostraTutti = false;
        },
        /* ------------------------------------------------------------------
         * EXPORT: si riusa il motore report gia' presente nel progetto
         * (LIB/ISPRINT_LIB/Report.php: TCPDF per il PDF, Excel per xlsx),
         * raggiunto dall'endpoint /theb/report come fanno gli altri export.
         * Lo stesso report (ID_REPORT 1040) produce il PDF e l'Excel, quindi
         * modale, Excel e PDF derivano dallo STESSO dataset e dallo stesso
         * perimetro filtrato: nessuna logica economica duplicata.
         * ------------------------------------------------------------------ */
        /* Filtri correnti della Dashboard, passati al report con gli stessi nomi
           usati dall'API: il perimetro del file coincide cosi' con quello a schermo. */
        parametriFiltro() {
            const f = this.filtri || {};
            const p = [];
            const agg = (k, v) => {
                if (v === null || v === undefined || v === "" || v === 0 || v === "0") return;
                p.push("&" + k + "=" + encodeURIComponent(String(v)));
            };
            agg("date_from", f.date_from);
            agg("date_to", f.date_to);
            agg("account_id", f.account_id);
            agg("client_type", f.client_type);
            agg("client_id", f.client_id);
            agg("state_id", f.state_id);
            agg("tipology_id", f.tipology_id);
            agg("project_id", f.project_id);
            agg("location_id", f.location_id);
            agg("supplier_id", f.supplier_id);
            return p.join("");
        },
        /* Apre il download del file generato dal report. 1 = PDF, 2 = Excel. */
        apriReport(formato) {
            const url = global.utils.BASE_API + "theb/report?ID_REPORT=1040&FORMATO=" + formato
                + this.parametriFiltro()
                + global.utils.getSessionToken();
            window.open(url, "_blank");
        },
        esportaExcel() {
            this.apriReport(2);
        },
        esportaPdf() {
            this.apriReport(1);
        },        /* Costo del fornitore principale (valore numerico grezzo). */
        valorePrincipale() {
            const primo = this.righeDettaglio[0];
            return primo ? primo.importo : 0;
        },

    },
};
</script>


<style scoped>
/* UI-05A: respiro interno. Il grafico non tocca i bordi della card,
   l'header o il fondo; la card resta alta quanto il contenuto. */
.ui05-header {
    padding: 18px 20px 12px;
}

.ui05-body {
    padding: 4px 20px 20px;
}

/* Select azioni compatta, allineata al titolo e con spazio dal bordo destro. */
.ui05-azioni {
    width: auto;
    min-width: 132px;
    max-width: 160px;
    border-radius: 8px;
    font-size: .8rem;
    padding: .25rem 1.75rem .25rem .55rem;
    background-color: #fff;
}

/* Altezza dell'area di disegno: non e' "spazio bianco", e' il contenitore
   su cui VChart disegna. */
.ui05-chart {
    height: 320px;
}

.ui05-canvas {
    width: 100%;
    height: 100%;
}

/* UI-05: la modale resta dentro la viewport. Header, riepilogo e footer sono
   fissi: scorre SOLO l'area tabella, con header tabella sticky. */
.ui05-modal .modal-content {
    max-height: 85vh;
    display: flex;
    flex-direction: column;
}

.ui05-modal .modal-header,
.ui05-modal .modal-footer {
    flex-shrink: 0;
}

.ui05-modal-body {
    overflow-y: auto;
    flex: 1 1 auto;
}

.ui05-tabella-wrap {
    max-height: 52vh;
    overflow-y: auto;
}

/* Header tabella sticky mentre scorrono i fornitori. */
.ui05-tabella thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: #fff;
    box-shadow: inset 0 -1px 0 #e9edf2;
}

/* Mini riepilogo della drill modale. */
.ui05-riepilogo {
    display: flex;
    flex-direction: column;
    gap: 2px;
    padding: 10px 12px;
    border: 1px solid #e9edf2;
    border-radius: 10px;
    background-color: #fbfcfe;
    height: 100%;
}

.ui05-riepilogo-label {
    font-size: .72rem;
    text-transform: uppercase;
    letter-spacing: .03em;
    color: #94a3b8;
}

.ui05-riepilogo-valore {
    font-size: .92rem;
    font-weight: 700;
    color: #1f2d3d;
    overflow-wrap: anywhere;
}

@media (max-width: 575.98px) {
    .ui05-header {
        padding: 14px 16px 10px;
    }

    .ui05-body {
        padding: 4px 16px 16px;
    }
}

/* Altezza fissa per allineamento con Classifiche (420px). */
.ui05-body--fixed {
    height: 420px;
    display: flex;
    flex-direction: column;
    padding: 4px 20px 20px; /* mantieni padding originale */
}
.ui05-body--fixed > .ui05-chart--fill {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
}
.ui05-body--fixed > .ui05-chart--fill .ui05-canvas {
    width: 100%;
    height: 100%;
    flex: 1 1 auto;
}

/* Scrollbar per il body fixed se necessario */
.ui05-body--fixed {
    overflow: hidden;
}
</style>
